import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX } from "lucide-react";

// セッション内でミュート状態を保持（コンポーネント再マウントをまたぐ）
let sessionMuted = true;

interface LotteryAnimationProps {
  animationUrl?: string;
  isPlaying: boolean;
  onAnimationEnd: () => void;
  onSkipAll?: () => void; // 全てのアニメーションをスキップして結果を表示
  gachaCount: number; // 1 or 10
  currentIndex?: number; // 現在のアニメーションインデックス（0始まり）
  totalCount?: number; // 総アニメーション数
}

const fileTypeFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;

  const fragmentContentType = (url.split('#', 2)[1] || '')
    .split('&')
    .map((entry) => entry.split('='))
    .find(([key]) => key === 'content_type')?.[1];

  if (fragmentContentType) {
    const contentType = decodeURIComponent(fragmentContentType.replace(/\+/g, ' '));
    const types: Record<string, string> = {
      'application/json': 'json',
      'image/gif': 'gif',
      'video/mp4': 'mp4',
      'video/webm': 'webm',
    };
    return types[contentType];
  }

  return url.split(/[?#]/)[0]?.split('.').pop()?.toLowerCase();
};

const LotteryAnimation: React.FC<LotteryAnimationProps> = ({
  animationUrl,
  isPlaying,
  onAnimationEnd,
  onSkipAll,
  gachaCount,
  currentIndex,
  totalCount,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLottieLoaded, setIsLottieLoaded] = useState(false);
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(sessionMuted);

  useEffect(() => {
    if (!isPlaying) return;

    // アニメーションが設定されていない場合は1秒後に終了
    if (!animationUrl) {
      const timer = setTimeout(() => {
        onAnimationEnd();
      }, 1000);
      return () => clearTimeout(timer);
    }

    const fileType = fileTypeFromUrl(animationUrl);

    if (fileType === 'json') {
      // Lottie animation
      loadLottieAnimation();
    } else if (fileType === 'mp4' || fileType === 'webm') {
      // autoPlay で再生開始。ブロックされた場合は muted で再試行
      const video = videoRef.current;
      if (video) {
        video.play().catch(() => {
          video.muted = true;
          setIsMuted(true);
          sessionMuted = true;
          video.play().catch(() => onAnimationEnd());
        });
      }
    } else if (fileType === 'gif') {
      // GIF animation - auto-play after 3 seconds
      const timer = setTimeout(() => {
        onAnimationEnd();
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      // 未知の形式でも抽選結果画面を停止させない
      const timer = setTimeout(onAnimationEnd, 3000);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, animationUrl, currentIndex]);

  const loadLottieAnimation = async () => {
    try {
      // Dynamically import lottie-web only when needed
      const lottie = await import('lottie-web');

      if (lottieContainerRef.current && animationUrl) {
        // Clear previous animation
        lottieContainerRef.current.innerHTML = '';

        const animation = lottie.default.loadAnimation({
          container: lottieContainerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          path: animationUrl,
        });

        animation.addEventListener('complete', onAnimationEnd);
        setIsLottieLoaded(true);

        return () => {
          animation.removeEventListener('complete', onAnimationEnd);
          animation.destroy();
        };
      }
    } catch (error) {
      console.error('Failed to load Lottie animation:', error);
      // Fallback to ending animation after 3 seconds
      setTimeout(onAnimationEnd, 3000);
    }
  };

  const handleVideoEnd = () => {
    onAnimationEnd();
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      const newMuted = !isMuted;
      video.muted = newMuted;
      setIsMuted(newMuted);
      sessionMuted = newMuted;
    }
  };

  if (!isPlaying) {
    return null;
  }

  const fileType = fileTypeFromUrl(animationUrl);
  const isVideo = fileType === 'mp4' || fileType === 'webm';

  return (
    <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
      {/* Skip buttons - top right */}
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        {isVideo && (
          <Button
            onClick={toggleMute}
            variant="ghost"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white text-xs"
          >
            {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
        )}
        <Button
          onClick={onAnimationEnd}
          variant="ghost"
          size="sm"
          className="bg-white/20 hover:bg-white/30 text-white text-xs"
        >
          動画をスキップ
        </Button>
        {onSkipAll && totalCount && totalCount > 1 && (
          <Button
            onClick={onSkipAll}
            variant="ghost"
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white text-xs"
          >
            結果を見る
          </Button>
        )}
      </div>

      {/* Gacha count indicator */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 text-white text-xl font-bold z-10">
        {totalCount && totalCount > 1 ? (
          <span>{currentIndex !== undefined ? currentIndex + 1 : 1} / {totalCount}</span>
        ) : (
          <span>{gachaCount}回ガチャ</span>
        )}
      </div>

      {/* Animation content */}
      {!animationUrl ? (
        /* アニメーションが設定されていない場合のデフォルト表示 */
        <div className="w-full h-full flex flex-col items-center justify-center">
          <div className="text-white text-2xl mb-4">🎲</div>
          <div className="text-white text-lg">抽選中...</div>
        </div>
      ) : (
        <>
          {fileType === 'json' && (
            <div
              ref={lottieContainerRef}
              className="w-full h-full max-w-2xl"
            />
          )}

          {isVideo && (
            <video
              key={`${animationUrl}-${currentIndex}`}
              ref={videoRef}
              src={animationUrl}
              className="w-full h-full object-cover"
              onEnded={handleVideoEnd}
              playsInline
            />
          )}

          {fileType === 'gif' && (
            <img
              src={animationUrl}
              alt="Lottery Animation"
              className="w-full h-full object-cover"
            />
          )}

          {/* Loading state */}
          {fileType === 'json' && !isLottieLoaded && (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-white text-lg">アニメーション読み込み中...</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LotteryAnimation;
